import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";

interface GalleryCarouselProps {
  images: string[];
  businessName: string;
}

const GalleryCarousel: React.FC<GalleryCarouselProps> = ({
  images,
  businessName,
}) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);

  const nextImage = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const nextLightboxImage = () => {
    setLightboxIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevLightboxImage = () => {
    setLightboxIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  if (!images || images.length === 0) {
    return (
      <div className="w-full h-[400px] bg-slate-200 rounded-xl flex items-center justify-center">
        <span className="text-slate-500">Tidak ada gambar tersedia</span>
      </div>
    );
  }

  return (
    <>
      <div className="relative w-full flex flex-col items-center">
        {/* === Gambar Utama (Landscape) === */}
        <div
          className="relative rounded-2xl overflow-hidden shadow-lg border border-slate-200 cursor-pointer hover:shadow-xl transition-shadow duration-300"
          style={{
            width: "100%",
            maxWidth: "900px",
            aspectRatio: "16 / 9",
          }}
          onClick={() => openLightbox(currentIndex)}
        >
          <img
            src={images[currentIndex]}
            alt={`${businessName} - Gambar ${currentIndex + 1}`}
            className="w-full h-full object-cover"
          />

          {/* Overlay hint untuk klik */}
          <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              whileHover={{ opacity: 1 }}
              className="bg-white/90 px-4 py-2 rounded-full text-sm font-medium text-slate-700"
            >
              Klik untuk memperbesar
            </motion.div>
          </div>

          {/* Tombol navigasi kiri-kanan */}
          {images.length > 1 && (
            <>
              <motion.button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Gambar sebelumnya"
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur p-3 rounded-full shadow-lg hover:bg-white transition-all duration-300 z-10"
              >
                <FiChevronLeft className="h-6 w-6 text-slate-700" />
              </motion.button>

              <motion.button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Gambar selanjutnya"
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur p-3 rounded-full shadow-lg hover:bg-white transition-all duration-300 z-10"
              >
                <FiChevronRight className="h-6 w-6 text-slate-700" />
              </motion.button>
            </>
          )}

          {/* Indikator posisi */}
          {images.length > 1 && (
            <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1.5 rounded-full text-sm font-medium">
              {currentIndex + 1} / {images.length}
            </div>
          )}
        </div>

        {/* === Thumbnail Grid === */}
        {images.length > 1 && (
          <div className="w-full max-w-[900px] mt-6">
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-2">
              {images.map((image, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentIndex(index)}
                  className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all duration-300 ${
                    index === currentIndex
                      ? "border-primary-500 shadow-lg"
                      : "border-slate-200 hover:border-primary-300"
                  }`}
                >
                  <img
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {index === currentIndex && (
                    <div className="absolute inset-0 bg-primary-500/20" />
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* === Lightbox Modal === */}
      <AnimatePresence>
        {isLightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            {/* Close Button */}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={closeLightbox}
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors duration-300 z-10"
            >
              <FiX className="h-6 w-6 text-white" />
            </motion.button>

            {/* Image Container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative flex items-center justify-center w-full h-full px-20"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[lightboxIndex]}
                alt={`${businessName} - Detail ${lightboxIndex + 1}`}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                style={{ width: 'auto', height: 'auto' }}
              />

              {/* Navigation Buttons */}
              {images.length > 1 && (
                <>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={prevLightboxImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur p-4 rounded-full transition-all duration-300"
                  >
                    <FiChevronLeft className="h-8 w-8 text-white" />
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={nextLightboxImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur p-4 rounded-full transition-all duration-300"
                  >
                    <FiChevronRight className="h-8 w-8 text-white" />
                  </motion.button>
                </>
              )}

              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-medium">
                {lightboxIndex + 1} / {images.length}
              </div>
            </motion.div>

            {/* Info Text */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white/70 text-sm">
              Tekan ESC atau klik di luar gambar untuk menutup
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default GalleryCarousel;
